import React, { useEffect, useState } from 'react'
import { Accordion, Button, Centralizer, ChangeDialog, Container, DataTable, Grid, Group, InputTable, Select, Switch, TextField } from '../components'
import { CompanyType, companySchema } from '../types/Company'
import { deepCopyOf, isRequired, logger } from '../utils'
import { useAxiosPrivate, useJSONState, useStaticData, useTranslate } from '../hooks'
import { ZodError, set } from 'zod'
import { areIdentical } from '../utils'
import { AxiosError } from 'axios'
import { toast } from 'sonner'
import Priceplans from '../components/Priceplans/Priceplans'
import { FaSquareCheck, FaExclamation, FaCircleInfo } from "react-icons/fa6"
import { Link } from 'react-router-dom'

const Subscription = () => {
  const api = useAxiosPrivate()
  const { t, tZod } = useTranslate()
  const [changed, setChanged] = useState<boolean>(false) //Checks if form has changed
  const [company, setProp, setCompany] = useJSONState<CompanyType>()
  const [originalCompany, setOriginalCompany] = useState<CompanyType>()
  const [validationErrors, setValidationErrors] = useState<ZodError<CompanyType> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { staticData } = useStaticData()

  const [carriersuiteId, setCarriersuiteId ] = useState<string>()
  const [ carriersuites, setCarriersuites ] = useState<{name:string, value:string}[]>()

  const getCompany = async () => {
    try {
      const response = await api.get({
        endpoint: '/company',
        mount: null
      })
      setCompany(response.data)
      setOriginalCompany(response.data)
      setChanged(false)
    } catch (e) {
      console.error(e)
      // navigate('/login')
    }
  }

  const getInvoiceFile = async (invoiceId: string) => {
    try {
      const response = await api.get({
        endpoint: `/company/invoice/${invoiceId}`,
        mount: {
          loadingText: t("fetchingFile")
        }
      })

      if (response.data) {
        downloadFile(response.data.base64Data, response.data.fileName);
      }
    } catch (e) {
      console.error(e)
    }
  }

  const downloadFile = (base64Data: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${base64Data}`;
    link.download = fileName;
    link.click();
    setTimeout(() => link.remove(),500)    
  }

  useEffect(() => {
    getCompany()
    if(staticData){
      const cs = Object.keys(staticData.carriersuitesProducts ?? {}).reduce((prev, cur) => {
        prev.push({name: staticData.carriersuitesProducts[cur].name, value: cur})
        return prev
      }, [] as { name: string; value: string; }[])
      setCarriersuites(cs)
    }    
  }, [])

  useEffect(() => {
    setChanged(!areIdentical(company, originalCompany))
  }, [company, originalCompany])

  const handleSave = async (e: any) => {
    e.preventDefault()
    try {
      setError(null)
      setValidationErrors(null)
      const result = companySchema.safeParse(company)
      if (!result.success) {
        setValidationErrors(tZod(result.error))
        console.log(result.error)
        return false
      }

      await api.put({
        endpoint: '/company',
        data: company,
        mount: {
          loadingText: t("saving"),
          onLoadText: t("saved")
        }
      })
      setOriginalCompany(company)
    } catch (e) {
      console.error(e)

      if (e instanceof AxiosError) {
        const error = e.response?.data?.message || e.response?.data || e.message
        toast.error(error)
      } else {
        toast.error('An error occurred')
      }
    }
  }

  const handlePriceplanChange = async (planId: string, extraUsers: number, extraCarrierSuites: number, extraStorageDays: number) => {

    setCompany((prev: any) => (
      {
        ...prev,
        paymentPlanId: planId,
        nmbExtraUsers: extraUsers,
        nmbExtraTrp: extraCarrierSuites,
        saveExtraDays: extraStorageDays
      })
    )

  }

  const handleAddCarrier = async (carrierSuiteId: string) => {
    if (company.activeCarriers.length + 1 > (company?.nmbExtraTrp || 0) + 3)
      return toast.error(t("maxNumbersOfCarriers"));

    setProp(carrierSuiteId, "activeCarriers", company.activeCarriers.length)
  }

  function isDue(dateString: string) {
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return date < now;
  }

  if(!company) return <></>

  return <Container className='overflow-y-scroll overflow-x-hidden pb-32'>
    <form onSubmit={async (e) => { handleSave(e) }}>
      <Centralizer>
        <Container>
        <>
          {error && <p>{error}</p>}
              <Accordion label={t("account.subscription")} defaultOpen={true}>
                <Container style={{ maxWidth: "1000px" }}>
                  <Group label={t('address')}>
                    <Grid>
                      <TextField
                        className='w-full'
                        required={isRequired('name', companySchema) ? true : false}
                        value={company.name || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, name: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "name")?.message}
                      >{t("companyname")}
                      </TextField>
                      <TextField
                        className='w-full'
                        required={isRequired('street1', companySchema) ? true : false}
                        value={company.street1 || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, street1: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "street1")?.message}
                      >{t("street1")}
                      </TextField>
                      <TextField
                        className='w-full'
                        required={isRequired('street2', companySchema) ? true : false}
                        value={company.street2 || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, street2: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "street2")?.message}
                      >{t("street2")}
                      </TextField>
                      {/* <TextField
                        className='w-full'
                        required={isRequired('street3', companySchema) ? true : false}
                        value={company.street3 || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, street3: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "street3")?.message}
                      >{t("street3")}
                      </TextField> */}
                      <TextField
                        className='w-full md:w-24'
                        required={isRequired('zipCode', companySchema) ? true : false}
                        value={company.zipCode || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, zipCode: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "zipCode")?.message}
                      >{t("zipCode")}
                      </TextField>
                      <TextField
                        className='w-full'
                        required={isRequired('city', companySchema) ? true : false}
                        value={company.city || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, city: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "city")?.message}
                      >{t("city")}
                      </TextField>
                    </Grid>
                  </Group>
                  <Group label={t('account.contact')}>
                    <Grid>
                      <TextField
                        className='w-full'
                        required={isRequired('contactName', companySchema) ? true : false}
                        value={company.contactName || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, contactName: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "contactName")?.message}
                      >{t("contactName")}
                      </TextField>
                      <TextField
                        className='w-full'
                        required={isRequired('contactPhone', companySchema) ? true : false}
                        value={company.contactPhone || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, contactPhone: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "contactPhone")?.message}
                        _type='tel'
                      >{t("contactPhone")}
                      </TextField>
                      <TextField
                        className='w-full md:min-w-80'
                        required={isRequired('contactEmail', companySchema) ? true : false}
                        value={company.contactEmail || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, contactEmail: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "contactEmail")?.message}
                        _type='email'
                      >{t("contactEmail")}
                      </TextField>
                    </Grid>
                  </Group>
                  <Group label={t('account.contact_finance')}>
                    <Grid>
                      <TextField
                        className='w-full'
                        required={isRequired('financeContactName', companySchema) ? true : false}
                        value={company.financeContactName || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, financeContactName: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "financeContactName")?.message}
                      >{t("contactName")}
                      </TextField>
                      <TextField
                        className='w-full'
                        required={isRequired('financeContactPhone', companySchema) ? true : false}
                        value={company.financeContactPhone || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, financeContactPhone: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "financeContactPhone")?.message}
                        _type='tel'
                      >{t("contactPhone")}
                      </TextField>
                      <TextField
                        className='w-full md:min-w-80'
                        required={isRequired('financeContactEmail', companySchema) ? true : false}
                        value={company.financeContactEmail || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, financeContactEmail: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "financeContactEmail")?.message}
                        _type='email'
                      >{t("contactEmail")}
                      </TextField>
                    </Grid>
                  </Group>
                  <Group label={t('account.invoiceInfo')}>
                    <Grid>
                      <TextField
                        className='w-full md:min-w-80'
                        required={isRequired('emailInvoiceTo', companySchema) ? true : false}
                        value={company.emailInvoiceTo || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, emailInvoiceTo: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "emailInvoiceTo")?.message}
                        _type='email'
                      >{t("account.emailInvoiceTo")}
                      </TextField>
                      <TextField
                        className='w-full'
                        required={isRequired('orgNo', companySchema) ? true : false}
                        value={company.orgNo || ''}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, orgNo: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "orgNo")?.message}
                      >{t("orgNo")}
                      </TextField>
                      <TextField
                        className='w-full'
                        required={isRequired('vatNo', companySchema) ? true : false}
                        value={company.vatNo}
                        onChange={(e) => setCompany((prev: any) => ({ ...prev, vatNo: e.target.value }))}
                        error={validationErrors?.errors.find(e => e.path[0] === "vatNo")?.message}
                      >{t("vatNo")}
                      </TextField>
                    </Grid>
                  </Group>
                </Container>
              </Accordion>
              <Accordion label={t("account.warehouse")} defaultOpen={false}>
                <Container style={{ maxWidth: "1000px" }}>
                  <Group label={t('address')}>
                    <Switch 
                      label={t("sameAsBillingAddress")}
                      className="mb-4"
                      checked={company.warehouse.sameAsBillingAddress}
                      onChange={e => setProp(e.currentTarget.checked, "warehouse", "sameAsBillingAddress")}
                    />
                    {company.warehouse.sameAsBillingAddress ? <></> :
                    <>
                      <Grid>
                        <TextField
                          className='w-full'
                          required={true}
                          value={company.warehouse.name}
                          onChange={(e) => setProp(e.currentTarget.value, "warehouse", "name")}
                          error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "name")?.message}
                        >{t("companyname")}
                        </TextField>
                        <TextField
                          className='w-full'
                          required={true}
                          value={company.warehouse.street1}
                          onChange={(e) => setProp(e.currentTarget.value, "warehouse", "street1")}
                          error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "street1")?.message}
                        >{t("street1")}
                        </TextField>
                        <TextField
                          className='w-full'
                          value={company.warehouse.street2}
                          onChange={(e) => setProp(e.currentTarget.value, "warehouse", "street2")}
                          error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "street2")?.message}
                        >{t("street2")}
                        </TextField>
                        {/* <TextField
                          className='w-full'
                          value={company.warehouse.street3 || ''}
                          onChange={(e) => setProp(e.currentTarget.value, "warehouse", "street3")}
                          error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "street3")?.message}
                        >{t("street3")}
                        </TextField> */}
                        <TextField
                          className='w-full md:w-24'
                          required={true}
                          value={company.warehouse.zipCode}
                          onChange={(e) => setProp(e.currentTarget.value, "warehouse", "zipCode")}
                          error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "zipCode")?.message}
                        >{t("zipCode")}
                        </TextField>
                        <TextField
                          required={true}
                          value={company.warehouse.city}
                          onChange={(e) => setProp(e.currentTarget.value, "warehouse", "city")}
                          error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "city")?.message}
                        >{t("city")}
                        </TextField>                                              
                      </Grid>
                      <Group label={t('contact')}>
                        <Grid>
                          <TextField
                            className='w-full'
                            value={company.warehouse.contact?.name}
                            onChange={(e) => setProp(e.currentTarget.value, "warehouse", "contact", "name")}
                            error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "contact" && e.path[2] === "name")?.message}
                          >{t("name")}
                          </TextField>
                          <TextField
                            className='w-full'
                            value={company.warehouse.contact?.phoneNo}
                            onChange={(e) => setProp(e.currentTarget.value, "warehouse", "contact", "phoneNo")}
                            error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "contact" && e.path[2] === "phoneNo")?.message}
                            _type='tel'
                          >{t("phone")}
                          </TextField>
                          <TextField
                            className='w-full md:min-w-80'
                            value={company.warehouse.contact?.email}
                            onChange={(e) => setProp(e.currentTarget.value, "warehouse", "contact", "email")}
                            error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "contact" && e.path[2] === "email")?.message}
                            _type='email'
                          >{t("email")}
                          </TextField>
                        </Grid>
                      </Group>
                    </>
                    }                    
                  </Group>
                  <Container>
                    <Grid>
                    <TextField
                        _type='time'
                        value={company.warehouse.openingTime}
                        onChange={(e) => setProp(e.currentTarget.value, "warehouse", "openingTime")}
                        error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "openingTime")?.message}
                      >{t("openingTime")}
                      </TextField>
                      <TextField
                        _type='time'
                        value={company.warehouse.closingTime}
                        onChange={(e) => setProp(e.currentTarget.value, "warehouse", "closingTime")}
                        error={validationErrors?.errors.find(e => e.path[0] === "warehouse" && e.path[1] === "closingTime")?.message}
                      >{t("closingTime")}
                      </TextField>
                      <Select 
                        value={company.warehouse?.currency ?? ""} 
                        data={staticData?.currencies ?? []} 
                        label={t("currency")}
                        onChange={(e) => setProp(e.currentTarget.value, "warehouse", "currency")}
                      />
                    </Grid>
                  </Container>
                  <Group label={t("tradingAccounts")}>
                    <InputTable 
                      data={company.warehouse.tradingAccounts ?? []}
                      headers={{
                        type: {
                          type:"select", 
                          required:true, 
                          options: (value) => staticData?.tradingAccountTypes.filter(act => company.warehouse.tradingAccounts.findIndex(ta => ta.type == act.value && value != act.value) === -1)
                        }, 
                        number: {
                          type:"text", 
                          required: true
                        }
                      }}
                      addable={true}
                      deleteable={true}
                      setProp={setProp}
                      propPath={["warehouse", "tradingAccounts"]}
                    />
                  </Group>
                </Container>
              </Accordion>
              <Accordion label={t("account.priceplan")} defaultOpen={false}>                  
                  <Priceplans
                    hideHeader={true}                    
                    planId={company.paymentPlanId as string}
                    extraUsers={company.nmbExtraUsers as number}
                    extraCarrierSuites={company.nmbExtraTrp as number}
                    extraStorageDays={company.saveExtraDays as number}
                    currentNmbUsers={company.usersCount as number}
                    currentNmbCarrierSuites={company.activeCarriers?.length as number}
                    onPlanChange={handlePriceplanChange} 
                    maxCarriersuites={Object.keys(staticData?.carriersuitesProducts ?? {}).length}
                  />
                  <Switch label={t("account.autoAdjustPricePlan")} 
                    className='justify-self-center'
                    checked={company.autoAdjustPlan}
                    onChange={e => setProp(e.currentTarget.checked, "autoAdjustPlan")}
                    title={t("account.autoAdjustPricePlan_help")}
                  />
              </Accordion>
              <Accordion label={t("account.activeCarriers")} defaultOpen={false}>
                <Group>
                  <Grid>
                    <Select className='w-full dark:bg-schipt-darkest font-montserrat text-sm p-1'
                      value={carriersuiteId}
                      data={carriersuites?.filter(c => !company.activeCarriers.includes(c.value)) ?? []}
                      onChange={async (e) => handleAddCarrier(e.currentTarget.value)}
                    />
                  </Grid>
                  <DataTable data={company.activeCarriers?.map(c => {return {
                      name: staticData?.carriersuitesProducts[c]?.name, 
                      url: <Link to={t('carriersuiteFAQLink', {carriersuiteId:c})} target='_blank' rel='noreferrer'><FaCircleInfo /></Link>
                    }})}
                    deleteCallback={(i) => setProp(null,"activeCarriers", i, -1)}
                    hideHeaders={true}
                  />
                </Group>
              </Accordion>
              <Accordion label={t("account.invoices")} defaultOpen={false}>
                <Grid>
                  <DataTable
                    // className="w-full"
                    style={{ width: '100%' }}
                    translatePath='invoices'
                    data={company.invoices?.map((invoice) => {
                      return {
                            invoiceNo: invoice.invoiceNo,
                            invoiceDate: invoice.invoiceDate,
                            dueDate: <span style={{display:"flex", flexDirection:"row"}}>{invoice.dueDate} {isDue(invoice.dueDate) && invoice.invoiceStatus < 30 ? <><FaExclamation color="red"/><FaExclamation color="red"/><FaExclamation color="red"/></> : ''}</span>,
                            invoiceType: invoice.invoiceType,
                            invoiceStatus: <span style={{display:"flex", flexDirection:"row", gap:".3rem"}}>{
                              invoice.invoiceStatus == 30 ? 
                              <><FaSquareCheck color='limegreen'/>{t("paid")}</>:
                              <><FaExclamation color="red"/>{t("notPaid")}</>
                            }</span>,
                            invoiceTotalInclVAT: invoice.invoiceTotalInclVAT,
                            currencyCode: invoice.currencyCode,
                            sentTo: invoice.sentTo,
                            actions: invoice.hasFile ? (
                              <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => getInvoiceFile(invoice.invoiceNo)}>⬇️{t("download")}</span>
                            ) : (
                              <></>
                            )
                          }
                    }
                  )}                  
                  ></DataTable>
                </Grid>
              </Accordion>

              <ChangeDialog show={changed}
                undo={async () => {
                  setCompany(originalCompany as CompanyType)
                  setError(null)
                  setValidationErrors(null)
                }}
              />
            </>
        </Container>
      </Centralizer >
    </form>
  </Container>
}

export default Subscription